import { useState, useCallback, useEffect } from 'react';
import { searchAddress } from '../services/addressService';
import { formatAddress } from '../utils/formatters';

/**
 * Hook para gerenciar busca de endereços
 * Migrado de App.js para estrutura modular
 */
export const useAddressSearch = () => {
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressSearch, setAddressSearch] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [correctedAddress, setCorrectedAddress] = useState(null);
  const [originalAddress, setOriginalAddress] = useState(null);

  /**
   * Busca endereços com debounce
   */
  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    
    setIsLoadingAddress(true);
    try {
      const result = await searchAddress(query);
      if (result.success) {
        const formatted = result.items.map(item => {
          const formattedAddr = formatAddress(item, query);
          // Se o usuário digitou um número e ele não está no endereço formatado, adicionar
          let finalDisplay = formattedAddr || item.display_name;
          const numberMatch = query.match(/\b(\d+[A-Za-z]?|S\/N|s\/n)\b/);
          let extractedNumber = null;
          
          if (numberMatch && !finalDisplay.includes(numberMatch[1])) {
            const addr = item.address || {};
            const road = addr.road || addr.street || '';
            if (road) {
              finalDisplay = finalDisplay.replace(road, `${road}, ${numberMatch[1]}`);
              extractedNumber = numberMatch[1].toUpperCase() === 'S/N' ? '0' : numberMatch[1];
            }
          }
          
          // Extrair número do endereço se não foi extraído da query
          if (!extractedNumber) {
            const addr = item.address || {};
            extractedNumber = addr.house_number || addr.house_name || null;
            if (extractedNumber && extractedNumber.toUpperCase() === 'S/N') {
              extractedNumber = '0';
            }
          }
          
          return {
            display: finalDisplay,
            address: item.address || {},
            lat: item.lat,
            lon: item.lon,
            original: item.display_name,
            extractedNumber: extractedNumber // Preservar número extraído
          };
        });
        setAddressSuggestions(formatted);
      } else {
        setAddressSuggestions([]);
      }
    } catch (error) {
      console.error('Erro ao buscar endereços:', error);
      setAddressSuggestions([]);
    } finally {
      setIsLoadingAddress(false);
    }
  }, []);

  // Buscar endereços quando o termo de busca mudar (com debounce)
  useEffect(() => {
    if (!isEditingAddress || !addressSearch) {
      setAddressSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchAddresses(addressSearch);
    }, 500); // Debounce de 500ms

    return () => clearTimeout(timeoutId);
  }, [addressSearch, isEditingAddress, searchAddresses]);

  return {
    isEditingAddress,
    setIsEditingAddress,
    addressSearch,
    setAddressSearch,
    addressSuggestions,
    setAddressSuggestions,
    isLoadingAddress,
    correctedAddress,
    setCorrectedAddress,
    originalAddress,
    setOriginalAddress
  };
};

